import { create } from 'zustand'
import { login, logout, refresh, userinfo } from '../api/auth'
import {
	deleteAvatar as deleteAvatarApi,
	getProfile,
	updateProfile as updateProfileApi,
	uploadAvatar as uploadAvatarApi
} from '../api/user'
import { ProfileUpdatePayload, Token, User } from '../helpers/types/user'

type UnknownObject = Record<string, unknown>

const extractUserPatch = (payload: unknown): Partial<User> => {
	if (!payload || typeof payload !== 'object') return {}
	const obj = payload as UnknownObject

	if (obj.user && typeof obj.user === 'object') {
		return obj.user as Partial<User>
	}
	if (obj.data && typeof obj.data === 'object') {
		return obj.data as Partial<User>
	}

	return obj as Partial<User>
}

const mergeUser = (
	current: User | null,
	payload: unknown
): User | null => {
	const patch = extractUserPatch(payload)

	if (!current) {
		return Object.keys(patch).length ? (patch as User) : null
	}

	return {
		...current,
		...patch
	}
}

export interface AuthState {
	user: User | null
	isLoggedIn: boolean
	isFetched: boolean
	isTelegramUser: boolean
	profileLoading: boolean
	login: (username: string, password: string) => Promise<Token>
	logout: () => void
	refresh: () => Promise<void>
	userinfo: () => Promise<User>
	loadProfile: () => Promise<void>
	updateProfile: (payload: ProfileUpdatePayload) => Promise<User>
	uploadAvatar: (file: File) => Promise<User>
	deleteAvatar: () => Promise<User>
}

export const authStore = create<AuthState>()((set, get) => ({
	isLoggedIn: false,
	user: null,
	isFetched: false,
	isTelegramUser: false,
	profileLoading: false,

	login: async (username: string, password: string) => {
		const data = await login(username, password)
		set(() => ({ isLoggedIn: true, isFetched: true }))
		return data
	},
	logout: async () => {
		await logout()
		set(() => ({
			user: null,
			isLoggedIn: false,
			isFetched: true
		}))
	},
	refresh: async () => {
		await refresh()
		set(() => ({
			isLoggedIn: true,
			isFetched: true
		}))
	},
	userinfo: async () => {
		const data = await userinfo()
		set((state) => ({
			user: mergeUser(state.user, data),
			isFetched: true,
			...(state.isLoggedIn ? {} : { isLoggedIn: true })
		}))
		return data
	},
	loadProfile: async () => {
		set({ profileLoading: true })
		try {
			const data = await getProfile()
			const merged = mergeUser(get().user, data)
			set({
				user: merged,
				profileLoading: false
			})
		} catch {
			set({ profileLoading: false })
		}
	},
	updateProfile: async (payload: ProfileUpdatePayload) => {
		const data = await updateProfileApi(payload)
		const merged = mergeUser(get().user, data)
		set({ user: merged })
		return merged as User
	},
	uploadAvatar: async (file: File) => {
		const data = await uploadAvatarApi(file)
		const merged = mergeUser(get().user, data)
		set({ user: merged })
		return merged as User
	},
	deleteAvatar: async () => {
		const data = await deleteAvatarApi()
		const merged = mergeUser(get().user, data)
		set({ user: merged })
		return merged as User
	}
}))
